const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const SETTLE_MS = 140;

const CLONES_PER_SIDE = 2;

function initHeroSlideshow(root) {
  if (root.dataset.hdsReady === '1') return;

  const viewport = root.querySelector('[data-hds-viewport]');
  const track = root.querySelector('[data-hds-track]');
  if (!viewport || !track) return;

  const slides = Array.from(track.querySelectorAll('[data-hds-slide]'));
  if (slides.length === 0) return;

  root.dataset.hdsReady = '1';

  const dots = Array.from(root.querySelectorAll('[data-hds-dot]'));
  const prevBtn = root.querySelector('[data-hds-prev]');
  const nextBtn = root.querySelector('[data-hds-next]');

  const focusableSelector = 'a[href], button:not([disabled])';

  const total = slides.length;
  const loop = total > 1;
  const pad = loop ? Math.min(CLONES_PER_SIDE, total) : 0;

  const makeClone = (source) => {
    const clone = source.cloneNode(true);
    clone.removeAttribute('data-hds-slide');
    clone.removeAttribute('id');
    clone.setAttribute('aria-hidden', 'true');
    clone.dataset.hdsClone = '1';

    clone.removeAttribute('data-shopify-editor-block');
    clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => {
      el.removeAttribute('data-shopify-editor-block');
    });
    clone.querySelectorAll(focusableSelector).forEach((el) => {
      el.setAttribute('tabindex', '-1');
    });
    return clone;
  };

  if (loop) {

    for (let i = total - pad; i < total; i += 1) {
      track.insertBefore(makeClone(slides[((i % total) + total) % total]), slides[0]);
    }

    for (let i = 0; i < pad; i += 1) {
      track.appendChild(makeClone(slides[i % total]));
    }
  }

  const cells = Array.from(track.children);

  const realOf = (cellIndex) => (loop ? (((cellIndex - pad) % total) + total) % total : cellIndex);

  let cell = pad;
  let timer = null;
  let paused = false;
  let settleTimer = null;
  let programmaticUntil = 0;
  let scrollRaf = null;

  const applyState = () => {
    const real = realOf(cell);

    cells.forEach((el, i) => {
      el.classList.toggle('hds-slide--active', i === cell);
    });

    slides.forEach((slide, i) => {
      const active = i === real;
      if (active) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
      slide.querySelectorAll(focusableSelector).forEach((el) => {
        if (active) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });
    });

    dots.forEach((dot, i) => {
      const active = i === real;
      dot.classList.toggle('hds-dot--active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  };

  const scrollToCell = (i, instant) => {
    const el = cells[i];
    if (!el) return;
    const target = el.offsetLeft + el.offsetWidth / 2 - viewport.clientWidth / 2;
    const jump = instant || reduceMotion.matches;
    programmaticUntil = performance.now() + (jump ? 60 : 700);
    viewport.scrollTo({ left: Math.max(0, target), behavior: jump ? 'auto' : 'smooth' });
  };

  const goToCell = (next, instant) => {
    cell = Math.max(0, Math.min(cells.length - 1, next));
    applyState();
    scrollToCell(cell, instant);
  };

  const jumpToCell = (next) => {
    cells.forEach((el) => {
      el.style.transition = 'none';
    });
    goToCell(next, true);
    void track.offsetWidth;
    cells.forEach((el) => {
      el.style.transition = '';
    });
  };

  const nearestCell = () => {
    const mid = viewport.scrollLeft + viewport.clientWidth / 2;
    let closest = cell;
    let best = Infinity;
    cells.forEach((el, i) => {
      const distance = Math.abs(el.offsetLeft + el.offsetWidth / 2 - mid);
      if (distance < best) {
        best = distance;
        closest = i;
      }
    });
    return closest;
  };

  const settle = () => {
    if (performance.now() >= programmaticUntil) {
      const near = nearestCell();
      if (near !== cell) {
        cell = near;
        applyState();
      }
    }

    if (!loop) return;
    if (cell < pad) jumpToCell(cell + total);
    else if (cell >= total + pad) jumpToCell(cell - total);
  };

  const advance = () => {
    if (paused) return;
    goToCell(cell + 1);
  };

  const start = () => {
    if (timer) return;
    const interval = parseInt(root.dataset.autoplay || '', 10);
    if (!interval || reduceMotion.matches) return;

    if (document.hidden) return;
    timer = window.setInterval(advance, interval);
  };

  const stop = () => {
    if (!timer) return;
    window.clearInterval(timer);
    timer = null;
  };

  const restart = () => {
    stop();
    start();
  };

  prevBtn?.addEventListener('click', () => {
    goToCell(cell - 1);
    restart();
  });
  nextBtn?.addEventListener('click', () => {
    goToCell(cell + 1);
    restart();
  });
  dots.forEach((dot, i) => {
    dot.addEventListener('click', () => {
      goToCell(loop ? i + pad : i);
      restart();
    });
  });

  viewport.addEventListener(
    'scroll',
    () => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(settle, SETTLE_MS);

      if (scrollRaf) return;
      scrollRaf = window.requestAnimationFrame(() => {
        scrollRaf = null;
        if (performance.now() < programmaticUntil) return;
        const near = nearestCell();
        if (near !== cell) {
          cell = near;
          applyState();
        }
      });
    },
    { passive: true }
  );

  const pause = () => { paused = true; };
  const resume = () => { paused = false; };

  root.addEventListener('mouseenter', pause);
  root.addEventListener('mouseleave', resume);
  root.addEventListener('focusin', pause);
  root.addEventListener('focusout', (e) => {
    if (e.relatedTarget instanceof Node && root.contains(e.relatedTarget)) return;
    resume();
  });
  root.addEventListener('pointerdown', pause);
  window.addEventListener('pointerup', resume);

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stop();
    else start();
  });

  reduceMotion.addEventListener('change', (e) => {
    if (e.matches) stop();
    else start();
  });

  let resizeTimer = null;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => scrollToCell(cell, true), 100);
  });

  applyState();
  scrollToCell(cell, true);
  start();
}

document.querySelectorAll('[data-hds]').forEach((el) => {
  if (el instanceof HTMLElement) initHeroSlideshow(el);
});

(function () {
  function warm() {
    setTimeout(function () {
      document.querySelectorAll('[data-hds-slide] img[loading="lazy"]').forEach(function (img) {
        img.loading = 'eager';
      });
    }, 1500);
  }
  if (document.readyState === 'complete') warm();
  else window.addEventListener('load', warm, { once: true });
})();
